import { uploadImage } from "@/api/uploads";
import type { ImageAttachment } from "@/components/panels/image-preview-strip";
import { captureScreenshotsAsFiles } from "./use-screenshot-capture";

/** Where a mic button's recording is delivered instead of Denker. */
export interface CommentDictationTarget {
  id: string;
  /** True when the target must not trigger screenshot upload side effects. */
  disableScreenshotCapture?: boolean;
  deliverTranscript: (
    text: string,
    images: ImageAttachment[],
  ) => void | Promise<void>;
}

interface CommentDictationClaim {
  target: CommentDictationTarget;
  /** The recording this claim was made for has started. */
  bound: boolean;
}

let claim: CommentDictationClaim | null = null;
let surfaceClaim: CommentDictationTarget | null = null;

/** Keep a composer available to the global shortcut while its surface is open.
 * A mic claim made for the same recording still has priority. */
export function holdDictationSurface(target: CommentDictationTarget): void {
  surfaceClaim = target;
}

export function releaseDictationSurface(id: string): void {
  if (surfaceClaim?.id === id) surfaceClaim = null;
  if (claim?.target.id === id) claim = null;
}

/** A mic click claims the recording it is about to start for `target`. Only
 *  a mic button claims: the global voice shortcut (and any other start) never
 *  does, so those recordings always go to Denker. The claim lasts for that one
 *  recording — through a transcription error, until the status returns to
 *  idle — and the owner releases it then, or when it unmounts. */
export function claimCommentDictation(target: CommentDictationTarget): void {
  claim = { target, bound: false };
}

/** Called at every recording start. Binds a claim made for this start; any
 *  other claim belongs to an earlier recording and is dropped, so a recording
 *  begun by the global shortcut can never reach a composer. */
export function bindCommentDictationClaimToRecording(): void {
  if (claim && !claim.bound) {
    claim.bound = true;
    return;
  }
  claim = surfaceClaim ? { target: surfaceClaim, bound: true } : null;
}

/** A dictation anywhere recording belongs to no composer, not even one whose
 *  mic was clicked or whose surface is open: drop any claim. */
export function dropCommentDictationClaim(): void {
  claim = null;
}

export function releaseCommentDictationClaim(id: string): void {
  if (claim?.target.id === id) claim = null;
}

/** Takes the target of the recording whose transcript just arrived. */
export function takeCommentDictationClaim(): CommentDictationTarget | null {
  if (!claim?.bound) return null;
  const { target } = claim;
  claim = null;
  return target;
}

export function commentDictationClaimId(): string | null {
  return claim?.target.id ?? null;
}

export function commentDictationClaimedBy(id: string): boolean {
  return claim?.target.id === id;
}

export async function captureCommentScreenshots(): Promise<ImageAttachment[]> {
  const { files } = await captureScreenshotsAsFiles();
  if (files.length === 0) return [];

  return Promise.all(
    files.map(async (file) => {
      const localId = `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const uploaded = await uploadImage(file);
      return {
        id: localId,
        url: uploaded.url,
        uploading: false,
        imageId: uploaded.id,
        width: uploaded.width,
        height: uploaded.height,
        filename: uploaded.original_filename || file.name,
      };
    }),
  );
}
